import defaults from "lodash/defaults";
import isUndefined from "lodash/isUndefined";
import { createSelector } from "reselect";

import { identity } from "~/tools/identity";
import { Logger } from "~/tools/logger";
import { errorWithValueStatus, getExistingStatusValue, successStatus } from "~/tools/request-status";

import { createDeepEqualSelector } from "../../../../generic/redux/SelectorUtils";
import { validateSync } from "../../../../generic/Util";
import {
	type TimelineQuery,
	adjustTimeRangeAndTimestamp,
	timelineSchema,
} from "../../../../routes/timeline/TimelineQueryUtils";
import { selectWebUiConfig } from "../../config/ConfigSelectors";
import type { RootState } from "../../RootState";
import { selectAllSearchParams } from "../../route/RouteSelectors";

import type { TimelineState } from "./TimelineState";
import { LAST_6_HOURS, type SerializingStaticTimeRange, type TimeRange, getStaticTimeRange } from "./TimeRange";

export const logger = Logger.namespace("TimelineStateLogger");

const getTimelineState = (state: RootState): TimelineState => state.view.timeline;

export const getLastSelectedDynamicInterval = (state: RootState): TimeRange =>
	state.view.timeline.lastSelectedDynamicInterval;

export const selectTimelineQueryParams = createDeepEqualSelector(
	createSelector(selectAllSearchParams, ({ timestamp, timeRange }) => ({ timestamp, timeRange })),
	identity,
);

export const selectDefaultTimeRange = createSelector(selectWebUiConfig, (config) => {
	const value = config.defaultTimeRange as SerializingStaticTimeRange;
	return getStaticTimeRange(value) ?? LAST_6_HOURS;
});

export const selectTimelineStatus = createDeepEqualSelector(
	[selectTimelineQueryParams, selectDefaultTimeRange],
	(query, defaultTimeRange) => {
		const resultQuery = adjustTimeRangeAndTimestamp(defaults({}, query, { timeRange: defaultTimeRange }));
		const error = validateSync(timelineSchema, resultQuery);
		if (error != null) {
			logger.error(error);
			return errorWithValueStatus(error, {
				timeRange: defaultTimeRange,
			} as TimelineQuery);
		}
		return successStatus(resultQuery);
	},
);

export const selectTimestampDate: (state: RootState) => Date | undefined = createSelector(
	[selectTimelineStatus],
	(timelineStatus) => getExistingStatusValue(timelineStatus).timestamp,
);

export const isLive: (state: RootState) => boolean = createSelector([selectTimestampDate], isUndefined);

export const selectTimeRange: (state: RootState) => TimeRange = createSelector(
	[selectTimelineStatus, selectDefaultTimeRange],
	(timelineStatus, defaultTimeRange) => getExistingStatusValue(timelineStatus).timeRange ?? defaultTimeRange,
);

export const timestampInTimeRange: (state: RootState) => boolean = createSelector(
	[selectTimestampDate, selectTimeRange],
	(timestamp, timeRange) => (timestamp ? timeRange.contains(timestamp) : true),
);

export const selectTimestampHistory = createSelector([getTimelineState], (state) => state.timestampHistory);
